// WAV encoder for AudioBuffers. Supports 16/24-bit PCM and 32-bit float so we
// can keep professional fidelity (24-bit downloads; 32-bit float = exact,
// lossless round-trip of the in-memory float buffer for storage/transfer).
export type BitDepth = 16 | 24 | 32;

export function wavBytes(buffer: AudioBuffer, bitDepth: BitDepth = 24): Uint8Array {
  const numCh = buffer.numberOfChannels;
  const len = buffer.length;
  const rate = buffer.sampleRate;
  const isFloat = bitDepth === 32;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numCh * bytesPerSample;
  const dataSize = len * blockAlign;
  const ab = new ArrayBuffer(44 + dataSize);
  const dv = new DataView(ab);

  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i));
  };

  writeStr(0, "RIFF");
  dv.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  dv.setUint32(16, 16, true);
  dv.setUint16(20, isFloat ? 3 : 1, true); // 1=PCM, 3=IEEE float
  dv.setUint16(22, numCh, true);
  dv.setUint32(24, rate, true);
  dv.setUint32(28, rate * blockAlign, true);
  dv.setUint16(32, blockAlign, true);
  dv.setUint16(34, bitDepth, true);
  writeStr(36, "data");
  dv.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));

  let off = 44;
  for (let i = 0; i < len; i++) {
    for (let c = 0; c < numCh; c++) {
      const s = Math.max(-1, Math.min(1, channels[c][i]));
      if (isFloat) {
        dv.setFloat32(off, channels[c][i], true); off += 4;
      } else if (bitDepth === 24) {
        const v = Math.round(s < 0 ? s * 0x800000 : s * 0x7fffff);
        dv.setUint8(off, v & 0xff);
        dv.setUint8(off + 1, (v >> 8) & 0xff);
        dv.setUint8(off + 2, (v >> 16) & 0xff);
        off += 3;
      } else {
        dv.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true); off += 2;
      }
    }
  }
  return new Uint8Array(ab);
}

export function encodeWav(buffer: AudioBuffer, bitDepth: BitDepth = 24): Blob {
  return new Blob([wavBytes(buffer, bitDepth) as BlobPart], { type: "audio/wav" });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
